import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { DatatablesHelper } from 'src/app/helpers';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';
import { RouteService } from 'src/app/core/services';
import { AdminUser, Agency, LoggedInAdmin } from 'src/app/models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminInfoDialogComponent } from './admin-info-dialog/admin-info-dialog.component';

type UIAdmin = AdminUser & {
  agency?: Agency;
};

@Component({
  selector: 'app-admins',
  templateUrl: './admins.component.html',
  styleUrls: ['./admins.component.css']
})
export class AdminsComponent implements OnInit, OnDestroy {
  user!: LoggedInAdmin;
  admins: UIAdmin[] = [];
  loaded = false;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of admins can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService,
    private routeService: RouteService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.user = this.auth.user || {};
    this.initTable();
    this.loadAdmins();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip'
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'excel'
      ]
    });
  }

  private async loadAdmins() {
    const query = {
      getagency: true
    };

    const res: any = await this.adminService.getAll(query).toPromise();

    this.admins = res.admins;

    this.loadTable();
  }

  private loadTable() {
    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  async editAdminInfo(index: number) {
    const dialogConfig = new MatDialogConfig();
    const admin = this.admins[index];

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      // Gotta send all the property data to the dialog to prefill
      _id:       admin._id,
      email:     admin.email,
      firstName: admin.firstName,
      lastName:  admin.lastName,
    };

    // Open dialog
    const dialogRef = this.dialog.open(AdminInfoDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((adminInfo: any) => {
      if (!adminInfo) return;

      this.routeService.scrollToTop();

      // Update Property
      this.adminService.updateUser(adminInfo).toPromise().then((data: any) => {

        Object.assign(admin, data.user);

        // Rerender manually
        this.loadTable();

        // Success
        if (data.success) {
          // Display success
          this.flashMessage.show(data.message || "Admin info updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });
        }
      });
    });
  }

  async deleteAdmin(index: number) {
    const user = this.admins[index];

    if (confirm(`Are you sure you want to permanently delete admin ${user.email}?`) === false) {
      return;
    }

    // Delete User
    const data: any = await this.adminService.deleteAdmin(user._id).toPromise();

    // Reload table data
    await this.loadAdmins().catch(() => {});

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("Admin deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  async loginUnder(admin: AdminUser) {
    const role = admin.isSuperAdmin ? 'superAdmin' : 'agencyAdmin';
    const data = await this.adminService.impersonate(role, admin.email).toPromise();

    // User authenticated, store token
    if (data.success) {
      this.auth.storeAdminData(data.token, data.user);
      this.auth.storeAdminStatus(true);

      this.routeService.goToLandingPage();

      // Display log in to user
      this.flashMessage.show("You have been logged in under admin " + admin.email, {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  sortDate(value: any) {
    return value ? moment(value).startOf('day').unix() : 0;
  }
}
